import { useState, useEffect, useRef } from "react";

// ─── TUTORIAL OVERLAY ─────────────────────────────────────────────────────────
// Bulle toujours fixe juste au-dessus de la navbar.
// Spotlight seul qui se déplace sur l'élément cible via getBoundingClientRect.
// onEnter : action déclenchée en arrivant sur une étape

const STEPS = [
  {
    ref: "navHome",
    page: "home",
    title: "Onglet Muscu",
    desc: "C'est ta base. Tu retrouves ici ta séance du jour, tes programmes générés et tes programmes personnalisés.",
  },
  {
    ref: "startSession",
    page: "home",
    title: "Lancer une séance",
    desc: "Appuie sur \"Commencer\" pour démarrer la séance suggérée. Chaque série se valide au fur et à mesure.",
  },
  {
    ref: "tabGenerated",
    page: "home",
    title: "Programmes générés",
    desc: "Tes programmes sont générés automatiquement selon ton profil. Swipe vers la gauche pour y accéder — onglet Générés.",
    onEnter: "swipeToPrograms",
    onEnterTab: "generated",
  },
  {
    ref: "tabCustom",
    page: "home",
    title: "Programmes personnalisés",
    desc: "L'onglet Perso te permet de créer tes propres programmes, avec les exercices que tu veux.",
    onEnterTab: "custom",
  },
  {
    ref: "setActive",
    page: "home",
    title: "Définir comme actif",
    desc: "\"Définir comme actif\" affiche ce programme en priorité sur l'écran principal, avec ta prochaine séance automatique.",
  },
  {
    ref: "navTabata",
    page: "tabata",
    title: "Onglet Tabata",
    desc: "Le Tabata est une méthode HIIT : 20 secondes d'effort, 10 secondes de repos, sur plusieurs rounds. Excellent pour le cardio.",
  },
  {
    ref: "generateTabata",
    page: "tabata",
    title: "Générer un Tabata",
    desc: "Choisis la difficulté, la durée et les groupes musculaires — Calestan construit le programme et gère le chronomètre.",
  },
  {
    ref: "createTabata",
    page: "tabata",
    title: "Créer un Tabata",
    desc: "Tu préfères tout contrôler ? Construis ton Tabata manuellement avec les exercices de ton choix.",
  },
  {
    ref: "navSuivi",
    page: "suivi",
    title: "Onglet Suivi",
    desc: "Toute ta progression est ici — tonnage soulevé, séances passées, records personnels. Consulte l'évolution de chaque exercice et exporte tes données en CSV.",
  },
  {
    ref: "navProfile",
    page: "profile",
    title: "C'est parti !",
    desc: "Depuis le Profil, modifie ton programme, change le thème, active la voix et gère ton compte. Chaque séance te rapproche de ta meilleure version.",
    isLast: true,
  },
];

const SPOTLIGHT_PAD = 8;
const BUBBLE_WIDTH = 320;

export default function TutorialPage({ refs, onComplete, onSetPage, onSwipeToPrograms, onSwitchProgTab, C }) {
  const [step, setStep] = useState(0);
  const [spotRect, setSpotRect] = useState(null);
  const measureTimer = useRef(null);

  const current = STEPS[step];
  const isLast = !!current.isLast;
  const total = STEPS.length;

  useEffect(() => {
    if (current.page) onSetPage(current.page);
    if (current.onEnter === "swipeToPrograms" && onSwipeToPrograms) onSwipeToPrograms();
    if (current.onEnterTab && onSwitchProgTab) onSwitchProgTab(current.onEnterTab);

    clearTimeout(measureTimer.current);
    const delay = current.onEnter ? 400 : 150;
    measureTimer.current = setTimeout(() => {
      const el = refs[current.ref]?.current;
      if (!el) { setSpotRect(null); return; }
      setSpotRect(el.getBoundingClientRect());
    }, delay);

    return () => clearTimeout(measureTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    const onResize = () => {
      const el = refs[current.ref]?.current;
      if (!el) return;
      setSpotRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const next = () => { if (isLast) { onComplete(); return; } setStep(s => s + 1); };
  const skip = () => onComplete();

  const sr = spotRect ? {
    x: spotRect.left - SPOTLIGHT_PAD,
    y: spotRect.top - SPOTLIGHT_PAD,
    w: spotRect.width + SPOTLIGHT_PAD * 2,
    h: spotRect.height + SPOTLIGHT_PAD * 2,
    r: 10,
  } : null;

  const bubbleLeft = Math.max(16, (window.innerWidth - BUBBLE_WIDTH) / 2);
  const bubbleBottom = "calc(env(safe-area-inset-bottom) + 92px)";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9000, pointerEvents: "all" }}>

      {/* Overlay SVG avec trou spotlight */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="tuto-mask">
            <rect width="100%" height="100%" fill="white" />
            {sr && <rect x={sr.x} y={sr.y} width={sr.w} height={sr.h} rx={sr.r} ry={sr.r} fill="black" />}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.75)" mask="url(#tuto-mask)" />
        {sr && (
          <rect x={sr.x} y={sr.y} width={sr.w} height={sr.h} rx={sr.r} ry={sr.r}
            fill="none" stroke={C.accent} strokeWidth="2" opacity="0.9" />
        )}
      </svg>

      {/* Bulle fixe au-dessus de la navbar */}
      <div style={{
        position: "fixed",
        left: bubbleLeft,
        bottom: bubbleBottom,
        width: BUBBLE_WIDTH,
        background: C.card,
        border: `1px solid ${C.accent}50`,
        borderRadius: 18,
        padding: "16px 16px 14px",
        boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px ${C.accent}15`,
        zIndex: 9001,
        boxSizing: "border-box",
      }}>
        <p style={{ color: C.accent, fontSize: 10, fontWeight: 700, letterSpacing: 2, margin: "0 0 5px" }}>
          ÉTAPE {step + 1} / {total}
        </p>
        <h3 style={{ color: C.text, fontSize: 15, fontWeight: 900, margin: "0 0 5px", lineHeight: 1.2 }}>
          {current.title}
        </h3>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.55, margin: "0 0 12px" }}>
          {current.desc}
        </p>
        <div style={{ display: "flex", gap: 5, justifyContent: "center", marginBottom: 12 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 18 : 6, height: 6, borderRadius: 3,
              background: i === step ? C.accent : i < step ? `${C.accent}50` : C.faint,
              transition: "all 0.3s",
            }} />
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {!isLast && (
            <button onClick={skip} style={{
              flex: 1, padding: "9px", background: "transparent",
              border: `1px solid ${C.border}`, borderRadius: 10,
              color: C.muted, fontWeight: 600, fontSize: 12, cursor: "pointer",
            }}>Passer</button>
          )}
          <button onClick={next} style={{
            flex: 2, padding: "10px",
            background: `linear-gradient(135deg,${C.accent},${C.accent2})`,
            border: "none", borderRadius: 10,
            color: "white", fontWeight: 800, fontSize: 13, cursor: "pointer",
            boxShadow: `0 4px 14px ${C.glow}`,
          }}>
            {isLast ? "C'est parti !" : "Suivant →"}
          </button>
        </div>
      </div>
    </div>
  );
}
